import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import { 
  MapPin, 
  Battery, 
  Clock, 
  RefreshCw,
  Users,
  ChevronRight,
  X,
  Navigation,
  Zap
} from 'lucide-react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API = process.env.REACT_APP_BACKEND_URL;

// Zobox Brand Colors
const ZOBOX_BLUE = '#2b3380';

// Custom marker icons for different statuses
const createMarkerIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background-color: ${color};
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12]
  });
};

const onPickupIcon = createMarkerIcon('#10b981'); // Green
const availableIcon = createMarkerIcon(ZOBOX_BLUE); // Blue
const offlineIcon = createMarkerIcon('#9ca3af'); // Gray

// Component to handle map centering
const MapController = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || 15);
    }
  }, [center, zoom, map]);
  
  return null;
};

const EngineerLiveTracking = ({ user, onLogout }) => {
  const [engineers, setEngineers] = useState([]);
  const [stats, setStats] = useState({ total: 0, online: 0, on_pickup: 0, offline: 0 });
  const [selectedEngineer, setSelectedEngineer] = useState(null);
  const [engineerDetail, setEngineerDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [includeOffline, setIncludeOffline] = useState(false);
  const [mapCenter, setMapCenter] = useState([28.6139, 77.2090]); // Default: Delhi
  const [mapZoom, setMapZoom] = useState(12);
  const mapRef = useRef(null);

  // Fetch engineer locations
  const fetchLocations = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/engineer-tracking/admin/all-engineers?include_offline=${includeOffline}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setEngineers(response.data.engineers || []);
        setStats(response.data.stats || { total: 0, online: 0, on_pickup: 0, offline: 0 });
        setLastUpdated(new Date());
        
        // Auto-center on first engineer if we have data
        const engineerList = response.data.engineers || [];
        if (engineerList.length > 0 && !selectedEngineer) {
          const validEngineer = engineerList.find(e => e.latitude && e.longitude);
          if (validEngineer) {
            setMapCenter([validEngineer.latitude, validEngineer.longitude]);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLoading(false);
    }
  }, [includeOffline, selectedEngineer]);

  // Fetch detailed info for selected engineer
  const fetchEngineerDetail = async (engineerId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/engineer-tracking/admin/engineer/${engineerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setEngineerDetail(response.data);
      }
    } catch (error) {
      console.error('Error fetching engineer detail:', error);
    }
  };

  // Auto-refresh every 30 seconds
  useEffect(() => {
    fetchLocations();
    
    if (autoRefresh) {
      const interval = setInterval(fetchLocations, 30000);
      return () => clearInterval(interval);
    }
  }, [fetchLocations, autoRefresh]);

  // Center map on engineer
  const centerOnEngineer = (engineer) => {
    if (engineer.latitude && engineer.longitude) {
      setMapCenter([engineer.latitude, engineer.longitude]);
      setMapZoom(16);
      setSelectedEngineer(engineer);
      fetchEngineerDetail(engineer.engineer_id);
    }
  };

  // Get marker icon based on status
  const getMarkerIcon = (engineer) => {
    if (engineer.status === 'on_pickup') return onPickupIcon;
    if (engineer.is_online) return availableIcon;
    return offlineIcon;
  };

  // Format time ago
  const timeAgo = (dateString) => {
    if (!dateString) return 'N/A';
    const seconds = Math.floor((new Date() - new Date(dateString)) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="h-[calc(100vh-64px)] flex flex-col" data-testid="engineer-live-tracking">
        {/* Header */}
        <div className="p-4 border-b bg-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: ZOBOX_BLUE }}>
                Live Engineer Tracking
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Real-time GPS tracking of field engineers
              </p>
            </div>
            <div className="flex items-center gap-4">
              {/* Stats */}
              <div className="flex gap-3">
                <div className="px-3 py-2 rounded-lg bg-green-50 border border-green-200" data-testid="stat-online">
                  <div className="text-xs text-green-600">Online</div>
                  <div className="text-lg font-bold text-green-700">{stats.online}</div>
                </div>
                <div className="px-3 py-2 rounded-lg bg-blue-50 border border-blue-200" data-testid="stat-on-pickup">
                  <div className="text-xs text-blue-600">On Pickup</div>
                  <div className="text-lg font-bold text-blue-700">{stats.on_pickup}</div>
                </div>
                <div className="px-3 py-2 rounded-lg bg-gray-50 border border-gray-200" data-testid="stat-offline">
                  <div className="text-xs text-gray-600">Offline</div>
                  <div className="text-lg font-bold text-gray-700">{stats.offline}</div>
                </div>
              </div>
              
              {/* Controls */}
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={includeOffline}
                    onChange={(e) => setIncludeOffline(e.target.checked)}
                    className="rounded"
                    data-testid="toggle-offline"
                  />
                  Show Offline
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    className="rounded"
                    data-testid="toggle-auto-refresh"
                  />
                  Auto Refresh
                </label>
                <button
                  onClick={fetchLocations}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Refresh"
                  data-testid="refresh-btn"
                >
                  <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} style={{ color: ZOBOX_BLUE }} />
                </button>
              </div>
            </div>
          </div>
          {lastUpdated && (
            <p className="text-xs text-gray-400 mt-2">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Map */}
          <div className="flex-1 relative">
            <MapContainer
              center={mapCenter}
              zoom={mapZoom}
              className="w-full h-full"
              ref={mapRef}
              data-testid="tracking-map"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapController center={mapCenter} zoom={mapZoom} />
              
              {engineers.map(engineer => (
                engineer.latitude && engineer.longitude && (
                  <Marker
                    key={engineer.engineer_id}
                    position={[engineer.latitude, engineer.longitude]}
                    icon={getMarkerIcon(engineer)}
                    eventHandlers={{
                      click: () => {
                        setSelectedEngineer(engineer);
                        fetchEngineerDetail(engineer.engineer_id);
                      }
                    }}
                  >
                    <Popup>
                      <div className="min-w-[180px]">
                        <div className="font-bold text-sm" style={{ color: ZOBOX_BLUE }}>
                          {engineer.engineer_name}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {engineer.status === 'on_pickup' ? '🚗 On Pickup' :
                           engineer.is_online ? '🟢 Online' : '⚫ Offline'}
                        </div>
                        {engineer.battery_level && (
                          <div className="text-xs text-gray-500">🔋 {engineer.battery_level}%</div>
                        )}
                        {engineer.current_pickup && (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <div className="text-xs text-gray-500">Current Pickup:</div>
                            <div className="text-xs font-medium">{engineer.current_pickup.device}</div>
                            <div className="text-xs text-gray-500">{engineer.current_pickup.customer_name}</div>
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                )
              ))}
            </MapContainer>
            
            {/* Legend */}
            <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3 text-xs z-[1000]">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span>On Pickup</span>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: ZOBOX_BLUE }}></div>
                <span>Available</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                <span>Offline</span>
              </div>
            </div>
          </div>

          {/* Sidebar - Engineer List */}
          <div className="w-80 bg-white border-l overflow-y-auto" data-testid="engineer-list">
            <div className="p-3 border-b sticky top-0 bg-white z-10">
              <h3 className="font-semibold text-gray-900">Engineers ({engineers.length})</h3>
            </div>
            
            {loading && engineers.length === 0 ? (
              <div className="p-8 text-center">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-gray-400" />
                <p className="text-sm text-gray-500">Loading...</p>
              </div>
            ) : engineers.length === 0 ? (
              <div className="p-8 text-center">
                <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No engineers online</p>
                <p className="text-xs text-gray-400 mt-1">
                  Engineers will appear here when they start the app
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {engineers.map(engineer => (
                  <div
                    key={engineer.engineer_id}
                    className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedEngineer?.engineer_id === engineer.engineer_id ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => centerOnEngineer(engineer)}
                    data-testid={`engineer-item-${engineer.engineer_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          engineer.status === 'on_pickup' ? 'bg-green-500' :
                          engineer.is_online ? 'bg-blue-500' : 'bg-gray-400'
                        }`} />
                        <div>
                          <p className="font-medium text-sm text-gray-900">{engineer.engineer_name}</p>
                          <p className="text-xs text-gray-500">
                            {engineer.status === 'on_pickup' ? 'On Pickup' :
                             engineer.is_online ? 'Available' : 'Offline'}
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                    
                    <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
                      {engineer.battery_level && (
                        <span className="flex items-center gap-1">
                          <Battery className="w-3 h-3" />
                          {engineer.battery_level}%
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {timeAgo(engineer.last_updated)}
                      </span>
                    </div>
                    
                    {engineer.current_pickup && (
                      <div className="mt-2 p-2 bg-green-50 rounded text-xs">
                        <p className="font-medium text-green-800">{engineer.current_pickup.device}</p>
                        <p className="text-green-600">{engineer.current_pickup.customer_name}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Detail Panel (when engineer selected) */}
          {selectedEngineer && engineerDetail && (
            <div className="w-80 bg-white border-l overflow-y-auto" data-testid="engineer-detail-panel">
              <div className="p-3 border-b sticky top-0 bg-white z-10 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Engineer Details</h3>
                <button
                  onClick={() => { setSelectedEngineer(null); setEngineerDetail(null); }}
                  className="text-gray-400 hover:text-gray-600"
                  data-testid="close-detail-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                {/* Engineer Info */}
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center text-white text-xl font-bold"
                       style={{ backgroundColor: ZOBOX_BLUE }}>
                    {selectedEngineer.engineer_name?.charAt(0) || 'E'}
                  </div>
                  <h4 className="font-semibold text-lg mt-2">{selectedEngineer.engineer_name}</h4>
                  <p className="text-sm text-gray-500">{selectedEngineer.engineer_id}</p>
                  <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium ${
                    selectedEngineer.status === 'on_pickup' ? 'bg-green-100 text-green-800' :
                    selectedEngineer.is_online ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedEngineer.status === 'on_pickup' ? '🚗 On Pickup' :
                     selectedEngineer.is_online ? '🟢 Available' : '⚫ Offline'}
                  </span>
                </div>

                {/* Current Location */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <h5 className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Current Location
                  </h5>
                  <p className="text-sm font-mono">
                    {selectedEngineer.latitude?.toFixed(6)}, {selectedEngineer.longitude?.toFixed(6)}
                  </p>
                  {selectedEngineer.accuracy && (
                    <p className="text-xs text-gray-500 mt-1">Accuracy: ±{selectedEngineer.accuracy?.toFixed(0)}m</p>
                  )}
                </div>

                {/* Battery */}
                {selectedEngineer.battery_level !== null && selectedEngineer.battery_level !== undefined && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <h5 className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center gap-1">
                      <Battery className="w-3 h-3" /> Battery
                    </h5>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            selectedEngineer.battery_level > 50 ? 'bg-green-500' :
                            selectedEngineer.battery_level > 20 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${selectedEngineer.battery_level}%` }}
                        />
                      </div>
                      <span className="text-sm font-semibold">{selectedEngineer.battery_level}%</span>
                      {selectedEngineer.is_charging && (
                        <Zap className="w-4 h-4 text-yellow-500" />
                      )}
                    </div>
                  </div>
                )}

                {/* Today's Activity */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <h5 className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center gap-1">
                    <Navigation className="w-3 h-3" /> Today's Activity
                  </h5>
                  <p className="text-2xl font-bold" style={{ color: ZOBOX_BLUE }}>
                    {engineerDetail.total_points_today || 0}
                  </p>
                  <p className="text-xs text-gray-500">location updates</p>
                </div>

                {/* Active Assignments */}
                {engineerDetail.active_assignments?.length > 0 && (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-500 uppercase mb-2">Active Pickups</h5>
                    <div className="space-y-2">
                      {engineerDetail.active_assignments.map(lead => (
                        <div key={lead.lead_id} className="bg-blue-50 rounded-lg p-3 text-sm">
                          <p className="font-medium">{lead.device_info?.brand} {lead.device_info?.model}</p>
                          <p className="text-gray-600">{lead.customer_info?.name}</p>
                          <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${
                            lead.status === 'EN_ROUTE' ? 'bg-yellow-100 text-yellow-800' :
                            lead.status === 'ARRIVED' ? 'bg-green-100 text-green-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {lead.status?.replace(/_/g, ' ')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default EngineerLiveTracking;
